/**
 * Template: EventListTemplate
 * Layout for event list page
 */

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus } from 'lucide-react';
import { EventFilters, EventsTable, EventListPagination } from '../components';
import type { Event, EventStatus } from '@/types/event.types';

interface EventListTemplateProps {
  // Data
  events: Event[];

  // State
  isLoading: boolean;
  canEdit: boolean;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  // Search & Filters
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilter: EventStatus | 'all';
  onStatusFilterChange: (value: EventStatus | 'all') => void;
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange?: (from: string | undefined, to: string | undefined) => void;

  // Actions
  onView: (event: Event) => void;
  onEdit: (event: Event) => void;
  onDelete: (event: Event) => void;
  onCreateNew: () => void;

  // Delete confirmation
  showDeleteDialog: boolean;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  deletingEvent: Event | null;
  isDeleting: boolean;
}

/**
 * Template: Layout for event list page
 * Orchestrates filters, table, and delete confirmation
 */
export function EventListTemplate({
  events,
  isLoading,
  canEdit,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  onView,
  onEdit,
  onDelete,
  onCreateNew,
  showDeleteDialog,
  onCloseDeleteDialog,
  onConfirmDelete,
  deletingEvent,
  isDeleting,
}: EventListTemplateProps) {
  return (
    <div className="container mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Eventos</h1>
          <p className="text-muted-foreground">
            Gestiona todos los eventos y sus inscripciones
          </p>
        </div>
        {canEdit && (
          <Button onClick={onCreateNew}>
            <Plus className="mr-2 size-4" />
            Nuevo Evento
          </Button>
        )}
      </div>

      {/* Filters */}
      <EventFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={onDateRangeChange}
      />

      {/* Table */}
      <EventsTable
        events={events}
        isLoading={isLoading}
        canEdit={canEdit}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      {/* Pagination */}
      {!isLoading && totalItems > 0 && (
        <EventListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={onCloseDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingEvent && (
                <>
                  Estás a punto de eliminar el evento{' '}
                  <strong>{deletingEvent.name}</strong>. Esta acción no se puede
                  deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
